import { Avatar, Card, Typography, Tabs, Row, Col } from 'antd';
import type { CSSProperties } from 'react';
import Meta from 'antd/es/card/Meta';
import React, { useState, useEffect, useCallback } from 'react';
import {
  formatToINR,
  getTotalEquitiesTransaction,
  getTotalEquitiesValue,
} from '../../../../Utils/utils';
import TopSummarySection from '../../TopSummarySection';
import AnalyticsContainer from './AnalyticsContainer';
import HoldingsList from './HoldingsList';
import { StockHolding } from './types';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import NudgeModel from '../../../../Models/Nudge';

const { TabPane } = Tabs;

// Define responsive breakpoints
const BREAKPOINTS = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

// Define sector types
interface SectorMetrics {
  name: string;
  avgPE: number;
  avgPB: number;
  avgROE: number;
  companies: readonly string[];  // Make companies array readonly in the type
}

interface SectorMap {
  [key: string]: SectorMetrics;
}

// Define sectors and their typical metrics
const SECTORS: SectorMap = {
  BANKING: {
    name: 'Banking & Financial Services',
    avgPE: 22,
    avgPB: 2.8,
    avgROE: 15,
    companies: ['HDFC Bank', 'ICICI Bank', 'SBI', 'Axis Bank', 'Kotak Bank']
  },
  IT: {
    name: 'Information Technology',
    avgPE: 28,
    avgPB: 8.5,
    avgROE: 25,
    companies: ['TCS', 'Infosys', 'HCL Tech', 'Wipro', 'Tech Mahindra']
  },
  FMCG: {
    name: 'Fast Moving Consumer Goods',
    avgPE: 45,
    avgPB: 12.5,
    avgROE: 28,
    companies: ['HUL', 'ITC', 'Nestle', 'Britannia', 'Dabur']
  },
  AUTO: {
    name: 'Automobiles',
    avgPE: 25,
    avgPB: 4.2,
    avgROE: 18,
    companies: ['Maruti Suzuki', 'Tata Motors', 'M&M', 'Hero Motocorp', 'Bajaj Auto']
  },
  PHARMA: {
    name: 'Pharmaceuticals',
    avgPE: 32,
    avgPB: 5.5,
    avgROE: 20,
    companies: ['Sun Pharma', 'Dr Reddy', 'Cipla', "Divi's Lab", 'Biocon']
  },
  ENERGY: {
    name: 'Oil & Gas',
    avgPE: 12,
    avgPB: 2.2,
    avgROE: 15,
    companies: ['Reliance', 'ONGC', 'IOC', 'BPCL', 'GAIL']
  },
  METAL: {
    name: 'Metals & Mining',
    avgPE: 10,
    avgPB: 1.8,
    avgROE: 12,
    companies: ['Tata Steel', 'JSW Steel', 'Hindalco', 'Coal India', 'NALCO']
  }
};

// Styles object for better organization
const styles: {
  container: CSSProperties;
  contentWrapper: CSSProperties;
  tabsContainer: (isMobile: boolean) => CSSProperties;
} = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    maxWidth: '100vw',
    overflow: 'hidden',
  },
  contentWrapper: {
    width: '100%',
    padding: '0 16px',
    boxSizing: 'border-box',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  tabsContainer: (isMobile: boolean) => ({
    marginTop: 20,
    '.ant-tabs-nav': {
      marginBottom: isMobile ? 8 : 16,
    },
    '.ant-tabs-tab': {
      padding: isMobile ? '8px 12px' : '12px 16px',
      fontSize: isMobile ? '14px' : '16px',
    },
  }),
};

type Props = {
  onBack: () => void;
};

// Declare module augmentation for the components that need responsive props
declare module './HoldingsList' {
  interface Props {
    holdings: StockHolding[];
    loading: boolean;
    isMobile?: boolean;
    isTablet?: boolean;
  }
}

interface PerformanceAttribution {
  overall: {
    totalValue: number;
    totalInvestment: number;
    overallReturn: number;
  };
  sectorPerformance: Record<string, {
    value: number;
    investment: number;
    return_pct: number;
    weight: number;
    contribution: number;
  }>;
  marketCapPerformance: Record<'LARGE' | 'MID' | 'SMALL' | 'MICRO', {
    value: number;
    investment: number;
    return_pct: number;
    weight: number;
    contribution: number;
  }>;
  stockPerformance: {
    topContributors: Array<{
      name: string;
      value: number;
      investment: number;
      return_pct: number;
      weight: number;
      contribution: number;
    }>;
    topDetractors: Array<{
      name: string;
      value: number;
      investment: number;
      return_pct: number;
      weight: number;
      contribution: number;
    }>;
  };
}

// Update the module declaration
declare module './AnalyticsContainer' {
  interface Props {
    holdings: StockHolding[];
    isMobile?: boolean;
    isTablet?: boolean;
    performanceAttribution?: PerformanceAttribution;
  }
}

// Helper function to get random sector
const getRandomSector = (): keyof typeof SECTORS => {
  const sectors = Object.keys(SECTORS);
  return sectors[Math.floor(Math.random() * sectors.length)] as keyof typeof SECTORS;
};

// Helper function to generate random dummy data
const generateDummyData = (sectorKey = getRandomSector()) => {
  const sector = SECTORS[sectorKey];
  const sectorPE = sector.avgPE * (0.8 + Math.random() * 0.4); // ±20% variation
  const sectorPB = sector.avgPB * (0.8 + Math.random() * 0.4);
  const sectorROE = sector.avgROE * (0.8 + Math.random() * 0.4);

  const generateQuarterlyData = () => ({
    revenue: (Math.random() * 10000 + 5000).toFixed(2),
    expenses: (Math.random() * 8000 + 4000).toFixed(2),
    ebitda: (Math.random() * 3000 + 1000).toFixed(2),
    depreciation: (Math.random() * 500 + 100).toFixed(2),
    interest: (Math.random() * 300 + 50).toFixed(2),
    tax: (Math.random() * 400 + 100).toFixed(2),
    netIncome: (Math.random() * 2000 + 500).toFixed(2),
    eps: (Math.random() * 15 + 2).toFixed(2),
    shares: Math.floor(Math.random() * 1000000000 + 100000000)
  });

  const generateProfitLoss = () => ({
    revenue: (Math.random() * 40000 + 20000).toFixed(2),
    operatingProfit: (Math.random() * 10000 + 5000).toFixed(2),
    pbt: (Math.random() * 8000 + 4000).toFixed(2),
    pat: (Math.random() * 6000 + 3000).toFixed(2),
    ebitda: (Math.random() * 12000 + 6000).toFixed(2),
    ebitdaMargin: (Math.random() * 30 + 10).toFixed(2),
    patMargin: (Math.random() * 20 + 5).toFixed(2),
    eps: (Math.random() * 50 + 10).toFixed(2)
  });

  const generateBalanceSheet = () => ({
    totalAssets: (Math.random() * 100000 + 50000).toFixed(2),
    currentAssets: (Math.random() * 40000 + 20000).toFixed(2),
    inventory: (Math.random() * 15000 + 5000).toFixed(2),
    receivables: (Math.random() * 10000 + 3000).toFixed(2),
    cash: (Math.random() * 8000 + 2000).toFixed(2),
    totalLiabilities: (Math.random() * 60000 + 30000).toFixed(2),
    currentLiabilities: (Math.random() * 25000 + 10000).toFixed(2),
    debt: (Math.random() * 30000 + 15000).toFixed(2),
    netWorth: (Math.random() * 40000 + 20000).toFixed(2)
  });

  const generateCashFlows = () => ({
    operatingCashFlow: (Math.random() * 8000 + 4000).toFixed(2),
    investingCashFlow: (-Math.random() * 6000 - 2000).toFixed(2),
    financingCashFlow: (-Math.random() * 4000 - 1000).toFixed(2),
    netCashFlow: (Math.random() * 2000 - 1000).toFixed(2)
  });

  const generateRatios = () => ({
    currentRatio: (Math.random() * 2 + 1).toFixed(2),
    quickRatio: (Math.random() * 1.5 + 0.5).toFixed(2),
    debtEquity: (Math.random() * 1.5).toFixed(2),
    interestCoverage: (Math.random() * 8 + 2).toFixed(2),
    debtorDays: Math.floor(Math.random() * 60 + 30),
    inventoryDays: Math.floor(Math.random() * 90 + 45),
    creditorDays: Math.floor(Math.random() * 75 + 30),
    workingCapitalDays: Math.floor(Math.random() * 120 + 60),
    assetTurnover: (Math.random() * 2 + 0.5).toFixed(2),
    roce: (Math.random() * 25 + 10).toFixed(2)
  });

  // Add specific generators for demat and dividend data
  const generateDematBreakup = () => [
    { 
      dematId: 'Zerodha', 
      quantity: Math.floor(Math.random() * 1000) + 100, 
      dpName: 'Zerodha Broking Ltd',
      dpId: 'IN303028',
      clientId: '12345678'
    },
    { 
      dematId: 'Groww', 
      quantity: Math.floor(Math.random() * 500) + 50, 
      dpName: 'Groww Financial Services',
      dpId: 'IN301135',
      clientId: '23456789'
    },
    { 
      dematId: 'Angel One', 
      quantity: Math.floor(Math.random() * 300) + 25, 
      dpName: 'Angel One Limited',
      dpId: 'IN300484',
      clientId: '34567890'
    },
    { 
      dematId: 'HDFC Securities', 
      quantity: Math.floor(Math.random() * 400) + 75, 
      dpName: 'HDFC Securities Ltd',
      dpId: 'IN300126',
      clientId: '45678901'
    },
    { 
      dematId: 'ICICI Direct', 
      quantity: Math.floor(Math.random() * 350) + 60, 
      dpName: 'ICICI Securities Ltd',
      dpId: 'IN302201',
      clientId: '56789012'
    },
    { 
      dematId: 'Upstox', 
      quantity: Math.floor(Math.random() * 250) + 40, 
      dpName: 'RKSV Securities',
      dpId: 'IN303719',
      clientId: '67890123'
    }
  ];

  const generateDividends = () => [
    { 
      date: '2024-01-15', 
      amount: (Math.random() * 10).toFixed(2), 
      type: 'Final',
      percentage: (Math.random() * 200 + 100).toFixed(2)
    },
    { 
      date: '2023-09-20', 
      amount: (Math.random() * 8).toFixed(2), 
      type: 'Interim',
      percentage: (Math.random() * 150 + 50).toFixed(2)
    },
    { 
      date: '2023-06-10', 
      amount: (Math.random() * 6).toFixed(2), 
      type: 'Special',
      percentage: (Math.random() * 300 + 150).toFixed(2)
    }
  ];

  // Generate all the data
  const quarterlyResults = Array.from({ length: 20 }, generateQuarterlyData);
  const yearlyProfitLoss = Array.from({ length: 5 }, generateProfitLoss);
  const yearlyBalanceSheet = Array.from({ length: 5 }, generateBalanceSheet);
  const yearlyCashFlows = Array.from({ length: 5 }, generateCashFlows);
  const yearlyRatios = Array.from({ length: 5 }, generateRatios);
  const dematBreakup = generateDematBreakup();
  const dividendHistory = generateDividends();

  return {
    marketMetrics: {
      marketCap: Math.floor(Math.random() * 50000) + 1000,
      enterpriseValue: Math.floor(Math.random() * 60000) + 1500,
      numberOfShares: Math.floor(Math.random() * 1000000000 + 100000000),
      divYield: (Math.random() * 5).toFixed(2),
      eps: (Math.random() * 100).toFixed(2),
      pe: sectorPE.toFixed(2),
      pb: sectorPB.toFixed(2),
      roe: sectorROE.toFixed(2)
    },
    sector: {
      key: sectorKey,
      name: sector.name,
      metrics: {
        pe: sector.avgPE.toFixed(2),
        pb: sector.avgPB.toFixed(2),
        roe: sector.avgROE.toFixed(2)
      }
    },
    technicalIndicators: {
      fiftyTwoWeekHigh: Math.floor(Math.random() * 1000) + 500,
      fiftyTwoWeekLow: Math.floor(Math.random() * 500) + 100,
      rsi: Math.floor(Math.random() * 30) + 40, // More realistic RSI range
      macd: ((Math.random() * 10) - 5).toFixed(2),
      sma20: Math.floor(Math.random() * 1000) + 500,
      sma50: Math.floor(Math.random() * 1000) + 500,
      sma200: Math.floor(Math.random() * 1000) + 500,
      volume: Math.floor(Math.random() * 1000000) + 100000
    },
    shareholdingPattern: {
      promoters: (Math.random() * 30 + 30).toFixed(2), // 30-60%
      fii: (Math.random() * 20 + 10).toFixed(2), // 10-30%
      dii: (Math.random() * 15 + 10).toFixed(2), // 10-25%
      public: (Math.random() * 20 + 15).toFixed(2), // 15-35%
      others: (Math.random() * 10).toFixed(2), // 0-10%
      pledged: (Math.random() * 15).toFixed(2), // 0-15%
      lastUpdated: new Date().toISOString().split('T')[0]
    },
    financials: {
      quarterlyResults,
      profitAndLoss: yearlyProfitLoss,
      balanceSheet: yearlyBalanceSheet,
      cashFlows: yearlyCashFlows,
      ratios: yearlyRatios
    },
    dematBreakup,
    dividendHistory,
    peerComparison: sector.companies.map(company => ({
      companyName: company,
      marketCap: Math.floor(Math.random() * 50000) + 1000,
      pe: (sector.avgPE * (0.7 + Math.random() * 0.6)).toFixed(2), // ±30% variation
      pb: (sector.avgPB * (0.7 + Math.random() * 0.6)).toFixed(2),
      roe: (sector.avgROE * (0.7 + Math.random() * 0.6)).toFixed(2),
      ebitdaMargin: (Math.random() * 30 + 10).toFixed(2),
      patMargin: (Math.random() * 20 + 5).toFixed(2),
      debtEquity: (Math.random() * 1.5).toFixed(2)
    }))
  };
};

// Helper function to generate company analysis data
const generateCompanyAnalysis = () => {
  return {
    pros: [
      'Strong market position in core business',
      'Consistent revenue growth over the past 5 years',
      'Healthy balance sheet with low debt',
      'Experienced management team',
      'Favorable industry outlook'
    ],
    cons: [
      'High valuation compared to peers',
      'Dependent on a few key customers',
      'Regulatory risks in the sector',
      'Intense competition in the market'
    ],
    investmentRationale: 'This company has demonstrated strong growth potential with a solid track record of financial performance. The management has a clear vision for future expansion, and the company is well-positioned to capitalize on industry trends.',
    riskFactors: 'Key risks include economic slowdown, regulatory changes, and increased competition. The company may also face challenges related to supply chain disruptions and rising input costs.',
    competitiveAdvantages: 'The company benefits from strong brand recognition, economies of scale, and a diversified product portfolio. Its distribution network and customer relationships provide significant competitive moat.',
    growthCatalysts: 'Future growth is expected to be driven by new product launches, geographic expansion, and potential acquisitions. The company is also investing in digital transformation to improve operational efficiency.',
    valuationMetrics: {
      intrinsicValue: Math.floor(Math.random() * 1000) + 500,
      marginOfSafety: (Math.random() * 30).toFixed(2)
    },
    analystRecommendations: {
      buy: Math.floor(Math.random() * 15) + 5,
      hold: Math.floor(Math.random() * 10) + 3,
      sell: Math.floor(Math.random() * 5) + 1,
      averageTargetPrice: Math.floor(Math.random() * 1000) + 500
    },
    recentNews: [
      { date: '2023-05-15', headline: 'Company reports strong Q4 earnings', impact: 'POSITIVE', source: 'Financial Times' },
      { date: '2023-04-20', headline: 'New product launch announced', impact: 'POSITIVE', source: 'Business Standard' },
      { date: '2023-03-10', headline: 'Regulatory concerns raised by analysts', impact: 'NEGATIVE', source: 'Economic Times' }
    ]
  };
};

interface Nudge {
  type: string;
  message: string;
  severity: string;
  action: string;
  label?: string;
  ctaText?: string;
  ctaLink?: string;
  icon?: string;
  id?: string;
}

const generatePortfolioInsights = (holdings: StockHolding[]): {
  insights: Nudge[];
  performanceAttribution: PerformanceAttribution;
} => {
  if (!holdings.length) return {
    insights: [],
    performanceAttribution: {
      overall: { totalValue: 0, totalInvestment: 0, overallReturn: 0 },
      sectorPerformance: {},
      marketCapPerformance: {
        LARGE: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
        MID: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
        SMALL: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
        MICRO: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 }
      },
      stockPerformance: { topContributors: [], topDetractors: [] }
    }
  };

  // Calculate total values with safeguards against NaN
  const totalValue = holdings.reduce((sum, stock) => sum + (Number(stock.currentValue) || 0), 0);
  const totalInvestment = holdings.reduce((sum, stock) => sum + (Number(stock.totalInvestment) || 0), 0);
  
  // Calculate overall return with safeguard against division by zero
  const overallReturn = totalInvestment !== 0 ? ((totalValue - totalInvestment) / totalInvestment) * 100 : 0;

  // Calculate sector-wise performance attribution
  const sectorPerformance = holdings.reduce((acc, stock) => {
    const sector = stock.fundamentals.sector;
    const value = Number(stock.currentValue) || 0;
    const investment = Number(stock.totalInvestment) || 0;
    const return_pct = investment !== 0 ? ((value - investment) / investment) * 100 : 0;
    const weight = totalValue !== 0 ? (value / totalValue) * 100 : 0;
    const contribution = (weight * return_pct) / 100;

    if (!acc[sector]) {
      acc[sector] = {
        value: 0,
        investment: 0,
        return_pct: 0,
        weight: 0,
        contribution: 0
      };
    }
    acc[sector].value += value;
    acc[sector].investment += investment;
    acc[sector].weight = totalValue !== 0 ? (acc[sector].value / totalValue) * 100 : 0;
    acc[sector].return_pct = acc[sector].investment !== 0 ? 
      ((acc[sector].value - acc[sector].investment) / acc[sector].investment) * 100 : 0;
    acc[sector].contribution = (acc[sector].weight * acc[sector].return_pct) / 100;
    return acc;
  }, {} as Record<string, any>);

  // Calculate market cap-wise performance attribution
  const marketCapPerformance = holdings.reduce((acc, stock) => {
    const marketCap = stock.marketCap;
    const value = Number(stock.currentValue) || 0;
    const investment = Number(stock.totalInvestment) || 0;
    const return_pct = investment !== 0 ? ((value - investment) / investment) * 100 : 0;
    const weight = totalValue !== 0 ? (value / totalValue) * 100 : 0;
    const contribution = (weight * return_pct) / 100;

    if (!acc[marketCap]) {
      acc[marketCap] = {
        value: 0,
        investment: 0,
        return_pct: 0,
        weight: 0,
        contribution: 0
      };
    }
    acc[marketCap].value += value;
    acc[marketCap].investment += investment;
    acc[marketCap].weight = totalValue !== 0 ? (acc[marketCap].value / totalValue) * 100 : 0;
    acc[marketCap].return_pct = acc[marketCap].investment !== 0 ? 
      ((acc[marketCap].value - acc[marketCap].investment) / acc[marketCap].investment) * 100 : 0;
    acc[marketCap].contribution = (acc[marketCap].weight * acc[marketCap].return_pct) / 100;
    return acc;
  }, {
    LARGE: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
    MID: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
    SMALL: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
    MICRO: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 }
  } as Record<string, any>);

  // Calculate stock-wise performance attribution (top 10 contributors and detractors)
  const stockPerformance = holdings.map(stock => {
    const value = Number(stock.currentValue) || 0;
    const investment = Number(stock.totalInvestment) || 0;
    const return_pct = investment !== 0 ? ((value - investment) / investment) * 100 : 0;
    const weight = totalValue !== 0 ? (value / totalValue) * 100 : 0;
    const contribution = (weight * return_pct) / 100;

    return {
      name: stock.companyName,
      value,
      investment,
      return_pct,
      weight,
      contribution
    };
  }).sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

  // Add performance attribution data to holdings state
  const performanceAttribution = {
    overall: {
      totalValue,
      totalInvestment,
      overallReturn
    },
    sectorPerformance,
    marketCapPerformance,
    stockPerformance: {
      topContributors: stockPerformance.filter(stock => stock.contribution > 0).slice(0, 5),
      topDetractors: stockPerformance.filter(stock => stock.contribution < 0).slice(0, 5)
    }
  };

  // Calculate sector exposure with safeguard against NaN
  const sectorExposure = holdings.reduce((acc, stock) => {
    const value = Number(stock.currentValue) || 0;
    acc[stock.fundamentals.sector] = (acc[stock.fundamentals.sector] || 0) + value;
    return acc;
  }, {} as Record<string, number>);

  const maxSectorExposure = Math.max(...Object.values(sectorExposure));
  const dominantSector = Object.entries(sectorExposure).find(([_, value]) => value === maxSectorExposure)?.[0] || '';
  const sectorConcentration = totalValue !== 0 ? (maxSectorExposure / totalValue) * 100 : 0;

  // Calculate market cap distribution with safeguard against NaN
  const marketCapDistribution = holdings.reduce((acc, stock) => {
    const value = Number(stock.currentValue) || 0;
    const marketCapValue = Number(stock.fundamentals.marketCap) || 0;
    
    // Categorize based on market cap value (in crores)
    let category = 'LARGE';
    if (marketCapValue < 5000) {
      category = 'MICRO';  // < 5,000 Cr
    } else if (marketCapValue < 15000) {
      category = 'SMALL';  // 5,000 - 15,000 Cr
    } else if (marketCapValue < 50000) {
      category = 'MID';    // 15,000 - 50,000 Cr
    }                      // LARGE: > 50,000 Cr
    
    acc[category] = (acc[category] || 0) + value;
    return acc;
  }, {
    LARGE: 0,
    MID: 0,
    SMALL: 0,
    MICRO: 0
  } as Record<string, number>);

  // Calculate percentages for each market cap category
  const totalValueDistribution = Object.values(marketCapDistribution).reduce((sum, value) => sum + value, 0);
  const marketCapPercentages = Object.entries(marketCapDistribution).reduce((acc, [cap, value]) => {
    acc[cap] = totalValueDistribution > 0 ? (value / totalValueDistribution) * 100 : 0;
    return acc;
  }, {} as Record<string, number>);

  // Calculate average PE and PB ratios with safeguards against NaN
  const validPECount = holdings.filter(stock => Number(stock.fundamentals.pe) > 0).length || 1;
  const avgPE = holdings.reduce((sum, stock) => {
    const pe = Number(stock.fundamentals.pe) || 0;
    return sum + (pe > 0 ? pe : 0);
  }, 0) / validPECount;
  
  const validPBCount = holdings.filter(stock => Number(stock.fundamentals.pbRatio) > 0).length || 1;
  const avgPB = holdings.reduce((sum, stock) => {
    const pb = Number(stock.fundamentals.pbRatio) || 0;
    return sum + (pb > 0 ? pb : 0);
  }, 0) / validPBCount;

  // Calculate dividend yield with safeguard against NaN and division by zero
  const totalDividendYield = totalValue !== 0 ? holdings.reduce((sum, stock) => {
    const dps = Number(stock.fundamentals.dps) || 0;
    return sum + dps;
  }, 0) / totalValue * 100 : 0;

  const insights = [
    {
      type: 'PORTFOLIO_RETURN',
      message: `Your portfolio has ${overallReturn >= 0 ? 'gained' : 'lost'} ${Math.abs(overallReturn || 0).toFixed(1)}% overall`,
      severity: overallReturn >= 15 ? 'HIGH' : overallReturn >= 5 ? 'MEDIUM' : 'LOW',
      action: overallReturn < 0 ? 'Review underperforming stocks' : 'Consider booking partial profits'
    },
    {
      type: 'SECTOR_CONCENTRATION',
      message: `${dominantSector} sector comprises ${(sectorConcentration || 0).toFixed(1)}% of your portfolio`,
      severity: sectorConcentration > 40 ? 'HIGH' : sectorConcentration > 30 ? 'MEDIUM' : 'LOW',
      action: sectorConcentration > 30 ? 'Consider diversifying into other sectors' : 'Maintain sector balance'
    },
    {
      type: 'MARKET_CAP_DISTRIBUTION',
      message: `Portfolio Distribution: Large-Cap ${marketCapPercentages['LARGE'].toFixed(1)}%, Mid-Cap ${marketCapPercentages['MID'].toFixed(1)}%, Small-Cap ${marketCapPercentages['SMALL'].toFixed(1)}%, Micro-Cap ${marketCapPercentages['MICRO'].toFixed(1)}%`,
      severity: marketCapPercentages['LARGE'] < 40 ? 'HIGH' : marketCapPercentages['LARGE'] < 50 ? 'MEDIUM' : 'LOW',
      action: marketCapPercentages['LARGE'] < 40 ? 'Consider increasing large-cap allocation for stability' : 
             marketCapPercentages['MICRO'] > 15 ? 'High exposure to micro-caps, consider reducing risk' :
             'Maintain balanced market cap distribution'
    },
    {
      type: 'VALUATION_METRICS',
      message: `Portfolio PE (${(avgPE || 0).toFixed(1)}) is ${avgPE > 25 ? 'above' : avgPE < 15 ? 'below' : 'at'} market average`,
      severity: avgPE > 25 ? 'HIGH' : avgPE < 15 ? 'LOW' : 'MEDIUM',
      action: avgPE > 25 ? 'Review high PE stocks' : avgPE < 15 ? 'Look for value opportunities' : 'Monitor valuations'
    },
    {
      type: 'DIVIDEND_YIELD',
      message: `Portfolio dividend yield is ${(totalDividendYield || 0).toFixed(2)}%`,
      severity: totalDividendYield > 3 ? 'HIGH' : totalDividendYield > 1.5 ? 'MEDIUM' : 'LOW',
      action: totalDividendYield < 1.5 ? 'Consider dividend stocks for income' : 'Monitor dividend sustainability'
    }
  ];

  // Add stock-specific insights
  holdings.forEach(stock => {
    // High gainers
    if (stock.profitLoss.percentage > 50) {
      insights.push({
        type: 'STOCK_RETURN',
        message: `${stock.companyName} has gained over 50% - Consider rebalancing`,
        severity: 'HIGH',
        action: 'Review allocation'
      });
    } 
    // Significant losers
    else if (stock.profitLoss.percentage < -30) {
      insights.push({
        type: 'STOCK_LOSS',
        message: `${stock.companyName} is down more than 30% - Review holding`,
        severity: 'HIGH',
        action: 'Analyze fundamentals'
      });
    }
    
    // High PE stocks - ensure PE is a number
    const stockPE = Number(stock.fundamentals.pe) || 0;
    if (stockPE > 30) {
      insights.push({
        type: 'HIGH_VALUATION',
        message: `${stock.companyName} PE (${stockPE.toFixed(1)}) is significantly above average`,
        severity: 'MEDIUM',
        action: 'Review growth prospects'
      });
    }

    // High concentration
    const stockConcentration = (stock.currentValue / totalValue) * 100;
    if (stockConcentration > 15) {
      insights.push({
        type: 'HIGH_CONCENTRATION',
        message: `${stock.companyName} comprises ${stockConcentration.toFixed(1)}% of your portfolio`,
        severity: 'HIGH',
        action: 'Consider reducing exposure'
      });
    }

    // Low ROE - ensure ROE is a number
    const stockROE = Number(stock.fundamentals.roe) || 0;
    if (stockROE < 10) {
      insights.push({
        type: 'LOW_ROE',
        message: `${stock.companyName} has low ROE (${stockROE.toFixed(1)}%)`,
        severity: 'MEDIUM',
        action: 'Review profitability metrics'
      });
    }
  });

  return {
    insights,
    performanceAttribution
  };
};

const Stocks: React.FC<Props> = ({ onBack }) => {
  const [selectedTab, setSelectedTab] = useState('holdings');
  const [holdings, setHoldings] = useState<StockHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [performanceAttribution, setPerformanceAttribution] = useState<PerformanceAttribution>({
    overall: { totalValue: 0, totalInvestment: 0, overallReturn: 0 },
    sectorPerformance: {},
    marketCapPerformance: {
      LARGE: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
      MID: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
      SMALL: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 },
      MICRO: { value: 0, investment: 0, return_pct: 0, weight: 0, contribution: 0 }
    },
    stockPerformance: { topContributors: [], topDetractors: [] }
  });
  const { width } = useWindowSize();

  const isMobile = width ? width <= BREAKPOINTS.md : false;
  const isTablet = width ? width <= BREAKPOINTS.lg && width > BREAKPOINTS.md : false;

  const fetchHoldings = useCallback(async () => {
    setLoading(true);
    try {
      const transactions = await getTotalEquitiesTransaction();
      const formattedHoldings = transactions
        .filter((txn: any) => txn.units > 0)
        .map((txn: any, index: number) => {
          // Calculate sector key based on company name or randomly
          const sectorKey = Object.keys(SECTORS).find(
            (key) => SECTORS[key].companies.includes(txn.companyName)
          ) || Object.keys(SECTORS)[Math.floor(Math.random() * Object.keys(SECTORS).length)];

          // Generate random values for price and change
          const avgBuyPrice = txn.averageBuyPrice || Math.floor(Math.random() * 2000) + 100; // Random price between 100 and 2100
          const currentStockPrice = txn.currentStockPrice || avgBuyPrice * (0.7 + Math.random() * 0.6); // ±30% from avg price
          const units = txn.units || Math.floor(Math.random() * 1000) + 100; // Random units between 100 and 1100

          // Calculate market cap value and category
          const marketCapValue = txn.marketCap || Math.random() * 500000 + 1000;
          let marketCap: 'LARGE' | 'MID' | 'SMALL' | 'MICRO';
          
          // Distribute market caps based on index position or company name
          if (index % 4 === 0 || ['HDFC Bank', 'Reliance', 'TCS', 'Infosys'].includes(txn.companyName)) {
            marketCap = 'LARGE';  // 25% Large Cap
          } else if (index % 4 === 1 || ['Axis Bank', 'Tech Mahindra', 'HCL Tech'].includes(txn.companyName)) {
            marketCap = 'MID';    // 25% Mid Cap
          } else if (index % 4 === 2) {
            marketCap = 'SMALL';  // 25% Small Cap
          } else {
            marketCap = 'MICRO';  // 25% Micro Cap
          }

          // Adjust marketCapValue based on category
          const adjustedMarketCapValue = 
            marketCap === 'LARGE' ? Math.random() * 450000 + 50000 :  // 50,000 Cr+
            marketCap === 'MID' ? Math.random() * 35000 + 15000 :     // 15,000-50,000 Cr
            marketCap === 'SMALL' ? Math.random() * 10000 + 5000 :    // 5,000-15,000 Cr
            Math.random() * 4000 + 1000;                              // 1,000-5,000 Cr

          // Calculate P&L values
          const currentValue = units * currentStockPrice;
          const totalInvestment = units * avgBuyPrice;
          const absolutePL = currentValue - totalInvestment;
          const percentagePL = totalInvestment > 0 ? (absolutePL / totalInvestment) * 100 : 0;

          return {
            companyName: txn.companyName || SECTORS[sectorKey].companies[0],
            symbol: txn.symbol || txn.companyName || SECTORS[sectorKey].companies[0],
            type: txn.type || 'EQUITY',
            sector: SECTORS[sectorKey].name,
            quantity: units,
            avgBuyPrice: Number(avgBuyPrice.toFixed(2)),
            ltp: Number(currentStockPrice.toFixed(2)),
            currentValue: Number(currentValue.toFixed(2)),
            totalInvestment: Number(totalInvestment.toFixed(2)),
            profitLoss: {
              absolute: Number(absolutePL.toFixed(2)),
              percentage: Number(percentagePL.toFixed(2)),
              shortTerm: Number((absolutePL * 0.6).toFixed(2)),
              longTerm: Number((absolutePL * 0.4).toFixed(2))
            },
            marketCap,
            fundamentals: {
              sector: SECTORS[sectorKey].name,
              marketCap: adjustedMarketCapValue,
              pbRatio: txn.pbRatio || generateDummyData(sectorKey).marketMetrics.pb,
              eps: txn.eps || generateDummyData(sectorKey).marketMetrics.eps,
              pe: txn.pe || generateDummyData(sectorKey).marketMetrics.pe,
              sectorPE: SECTORS[sectorKey].avgPE,
              dps: txn.dps || generateDummyData(sectorKey).marketMetrics.divYield,
              salesGrowth: txn.salesGrowth || ((Math.random() * 20) + 5).toFixed(2),
              patGrowth: txn.patGrowth || ((Math.random() * 25) + 8).toFixed(2),
              sectorPB: SECTORS[sectorKey].avgPB,
              sectorROE: SECTORS[sectorKey].avgROE,
              sectorSalesGrowth: ((Math.random() * 15) + 8).toFixed(2),
              sectorPATGrowth: ((Math.random() * 18) + 10).toFixed(2),
              roe: txn.roe || generateDummyData(sectorKey).marketMetrics.roe,
              npa: sectorKey === 'BANKING' ? {
                gross: (Math.random() * 3 + 1).toFixed(2), // 1-4%
                net: (Math.random() * 1 + 0.3).toFixed(2), // 0.3-1.3%
              } : undefined,
              casaRatio: sectorKey === 'BANKING' ? (Math.random() * 20 + 35).toFixed(2) : undefined, // 35-55%
              nim: sectorKey === 'BANKING' ? (Math.random() * 1 + 2.5).toFixed(2) : undefined, // 2.5-3.5%
              pcr: sectorKey === 'BANKING' ? (Math.random() * 20 + 65).toFixed(2) : undefined // 65-85%
            },
            technicals: {
              fiftyTwoWeekHigh: txn.fiftyTwoWeekHigh || generateDummyData(sectorKey).technicalIndicators.fiftyTwoWeekHigh,
              fiftyTwoWeekLow: txn.fiftyTwoWeekLow || generateDummyData(sectorKey).technicalIndicators.fiftyTwoWeekLow,
              rsi: txn.rsi || generateDummyData(sectorKey).technicalIndicators.rsi,
              macd: txn.macd || generateDummyData(sectorKey).technicalIndicators.macd,
              sma: txn.sma || generateDummyData(sectorKey).technicalIndicators.sma20,
              ema: txn.ema || generateDummyData(sectorKey).technicalIndicators.sma50
            },
            shareholding: {
              promoters: txn.promoterHolding || generateDummyData(sectorKey).shareholdingPattern.promoters,
              fii: txn.fiiHolding || generateDummyData(sectorKey).shareholdingPattern.fii,
              dii: txn.diiHolding || generateDummyData(sectorKey).shareholdingPattern.dii,
              public: txn.publicHolding || generateDummyData(sectorKey).shareholdingPattern.public,
              lastUpdated: txn.holdingLastUpdated || generateDummyData(sectorKey).shareholdingPattern.lastUpdated
            },
            dematBreakup: txn.dematBreakup || generateDummyData(sectorKey).dematBreakup,
            dividends: txn.dividends || generateDummyData(sectorKey).dividendHistory,
            sectorPerformance: txn.sectorPerformance || {
              oneMonth: ((Math.random() * 16) - 8).toFixed(2), // -8% to +8%
              threeMonths: ((Math.random() * 24) - 12).toFixed(2), // -12% to +12%
              sixMonths: ((Math.random() * 30) - 15).toFixed(2), // -15% to +15%
              oneYear: ((Math.random() * 40) - 20).toFixed(2) // -20% to +20%
            },
            companyAnalysis: generateCompanyAnalysis()
          };
        });
      setHoldings(formattedHoldings);
    } catch (error) {
      console.error('Error fetching holdings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  useEffect(() => {
    if (holdings.length > 0) {
      const { performanceAttribution: newPerformanceAttribution } = generatePortfolioInsights(holdings);
      setPerformanceAttribution(newPerformanceAttribution);
    }
  }, [holdings]);

  return (
    <div style={styles.container}>
      <TopSummarySection
        isBackButton={true}
        onBack={onBack}
        subTitle={` ${formatToINR((getTotalEquitiesValue() || 0).toString())}`}
        title={isMobile ? 'Stocks' : 'Total Indian Stocks'}
        showInvestmentHeader={true}
      />

      <div style={styles.contentWrapper}>
        <Tabs
          activeKey={selectedTab}
          onChange={setSelectedTab}
          style={styles.tabsContainer(isMobile)}
          size={isMobile ? 'small' : 'middle'}
          tabBarGutter={isMobile ? 16 : 32}
        >
          <TabPane 
            tab={
              <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                Holdings
              </span>
            } 
            key="holdings"
          >
            <HoldingsList 
              holdings={holdings} 
              loading={loading}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </TabPane>
          <TabPane 
            tab={
              <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                Analytics
              </span>
            } 
            key="analytics"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <AnalyticsContainer
                  holdings={holdings}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  performanceAttribution={performanceAttribution}
                />
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default Stocks;
